/**
 * A confirmed-but-not-yet-paid online order attempt, remembered per browser
 * tab so retrying the exact same order (back from the payment gateway, page
 * reload) reopens the existing payment session instead of calling
 * POST /receipt/confirm again. Confirming twice creates a second receipt for
 * the same order, which reaches the restaurant as a duplicate.
 *
 * sessionStorage is deliberate: it survives the redirect to the payment
 * gateway and back, but dies with the tab. All access is try/catch-guarded —
 * some webviews block storage entirely, in which case the guard is simply off.
 */

/**
 * Exported so tests assert against the real key rather than a literal that keeps
 * passing after a rename.
 */
export const STORAGE_KEY = "prepit-pending-online-payment";

/** Payment sessions go stale quickly; past this age a fresh confirm is issued. */
const MAX_AGE_MS = 10 * 60 * 1000;

type PendingOnlinePayment = {
  /** JSON of the confirm request body — same fingerprint means same order. */
  fingerprint: string;
  paymentLink: string;
  receiptId?: number;
  createdAtMs: number;
  /**
   * When this document was actually torn down for the trip to the gateway.
   *
   * Set only from a real `pagehide`, so its presence is proof the browser left.
   * Until it is set, the customer never reached the gateway and there is nothing
   * to come back from — see `armGatewayDepartureTracking`.
   */
  departedAtMs?: number;
  /**
   * Part of the order is held on the customer's wallet. Coming back unpaid
   * must release that hold too: the backend only does on a failed Paymob
   * callback, not when the customer simply backs out.
   */
  usesWallet?: boolean;
};

/**
 * A wallet order's stamp: remembered for the way back, never matched as a
 * session to reuse. Its confirm fingerprint is "", which matches nothing, and
 * this is the only fingerprint it is ever stored under.
 */
export const walletOrderFingerprint = (receiptId: number): string =>
  `wallet-order:${receiptId}`;

export const buildConfirmFingerprint = (requestBody: unknown): string => {
  try {
    return JSON.stringify(requestBody);
  } catch {
    return "";
  }
};

export const getReusablePendingOnlinePayment = (
  fingerprint: string
): PendingOnlinePayment | null => {
  if (!fingerprint) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const stored = JSON.parse(raw) as PendingOnlinePayment;
    if (
      stored.fingerprint === fingerprint &&
      stored.paymentLink &&
      Date.now() - stored.createdAtMs < MAX_AGE_MS
    ) {
      return stored;
    }
  } catch {
    // Blocked/corrupted storage — behave as if nothing was stored.
  }

  return null;
};

export const storePendingOnlinePayment = (
  payment: Omit<PendingOnlinePayment, "createdAtMs">
): void => {
  if (!payment.fingerprint || !payment.paymentLink) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...payment, createdAtMs: Date.now() })
    );
  } catch {
    // Blocked storage — the guard is simply off for this session.
  }
};

/**
 * Stamp the attempt with the moment this document was torn down.
 *
 * `pagehide` is still a synchronous-storage moment, so the write lands whether
 * the document is being unloaded outright or frozen into the back/forward
 * cache. Only the first departure counts: coming back and leaving again for the
 * same attempt must not reset it.
 */
const markGatewayDeparture = (): void => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }
    const stored = JSON.parse(raw) as PendingOnlinePayment;
    if (stored.departedAtMs) {
      return;
    }

    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...stored, departedAtMs: Date.now() })
    );
  } catch {
    // Blocked/corrupted storage — no departure is recorded, so the abandon
    // simply never fires and the order waits out its loyalty hold.
  }
};

/**
 * Watch for this document leaving, and call it immediately before handing the
 * browser to the gateway.
 *
 * Writing the stamp is NOT proof of a trip: it is written while we are still on
 * our own page, one line before the redirect is issued. Between that line and
 * the browser actually leaving, the page is alive and can mount, re-render or be
 * restored any number of times — and every one of those looked like "the
 * customer is back without paying" to `useReleaseAbandonedPayment`, which is how
 * orders were cancelled seconds after checkout while their owner was still at
 * the gateway typing a card number (receipts 2313999, 2317719).
 *
 * `pagehide` is the browser telling us this document is going away — an unload
 * for the gateway, or a freeze into the back/forward cache. It cannot fire
 * without a real navigation or app switch, which is exactly the property a
 * remount does not have.
 *
 * One-shot: the listener removes itself once it has recorded the departure.
 */
export const armGatewayDepartureTracking = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  function onPageHide() {
    markGatewayDeparture();
    window.removeEventListener("pagehide", onPageHide);
  }

  window.addEventListener("pagehide", onPageHide);
};

/**
 * Read the stored attempt without clearing it, whatever its fingerprint or age.
 *
 * Used when the customer lands back on the site without having paid: the record
 * exists only because we sent them to the gateway, so its presence on any page
 * that is not part of the payment flow means that trip did not go through.
 *
 * Reading without clearing is deliberate. The stamp is what stops a second
 * confirm creating a duplicate order, so it may only be dropped once the order
 * it points at is actually cancelled — see `clearPendingOnlinePayment`, which the
 * abandon caller invokes on success only.
 */
export const peekPendingOnlinePayment = (): PendingOnlinePayment | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as PendingOnlinePayment;
  } catch {
    // Blocked/corrupted storage — nothing to act on.
    return null;
  }
};

export const clearPendingOnlinePayment = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do.
  }
};
